import { useMemo, useState } from "react";
import { useLocale } from "../hooks/useLocale";
import { TablePagination, type TablePaginationProps } from "./TablePagination";
import styles from "./TableView.module.css";

interface Props {
  columns: string[];
  rows: unknown[][];
  emptyKey?: string;
  pagination?: TablePaginationProps;
}

type SortDir = "asc" | "desc";

function compareValues(a: unknown, b: unknown): number {
  if (a === null || a === undefined) {
    if (b === null || b === undefined) return 0;
    return -1;
  }
  if (b === null || b === undefined) return 1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

function formatCell(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") {
    return Number.isInteger(v) ? v.toString() : v.toFixed(2);
  }
  return String(v);
}

export function TableView({ columns, rows, emptyKey = "table.no_data", pagination }: Props) {
  const { t } = useLocale();
  const [sortBy, setSortBy] = useState<{ idx: number; dir: SortDir } | null>(null);

  const sortedRows = useMemo(() => {
    if (!sortBy) return rows;
    const { idx, dir } = sortBy;
    const copy = [...rows];
    copy.sort((a, b) => {
      const cmp = compareValues(a[idx], b[idx]);
      return dir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [rows, sortBy]);

  function toggleSort(idx: number) {
    setSortBy((prev) => {
      if (!prev || prev.idx !== idx) return { idx, dir: "asc" };
      if (prev.dir === "asc") return { idx, dir: "desc" };
      return null;
    });
  }

  if (!rows.length && !pagination) {
    return <div className={styles.empty}>{t(emptyKey)}</div>;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.tableScroll}>
        <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((c, i) => {
              const dir = sortBy?.idx === i ? sortBy.dir : null;
              return (
                <th key={c + i} onClick={() => toggleSort(i)}>
                  <span>
                    {c}
                    {dir === "asc" && " ▲"}
                    {dir === "desc" && " ▼"}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td key={ci}>{formatCell(cell)}</td>
              ))}
            </tr>
          ))}
        </tbody>
        </table>
        {rows.length === 0 && (
          <div className={styles.emptyPage}>{t(emptyKey)}</div>
        )}
      </div>
      {pagination ? (
        <TablePagination {...pagination} />
      ) : (
        <p className={styles.meta}>{t("table.rows_count", { count: rows.length })}</p>
      )}
    </div>
  );
}
